import { createContext, ReactNode, useContext, useMemo } from "react";

interface NestedSnippetContextValue {
  isNested: boolean;
  ancestorSnippetNames: Set<string>;
}

const defaultValue: NestedSnippetContextValue = {
  isNested: false,
  ancestorSnippetNames: new Set(),
};

const NestedSnippetContext = createContext<NestedSnippetContextValue>(defaultValue);

export const NestedSnippetProvider = ({
  children,
  snippetName,
}: {
  children: ReactNode;
  snippetName?: string;
}) => {
  const parent = useContext(NestedSnippetContext);

  const value = useMemo(() => {
    const newAncestors = new Set(parent.ancestorSnippetNames);
    if (snippetName) {
      newAncestors.add(snippetName);
    }
    return {
      isNested: true,
      ancestorSnippetNames: newAncestors,
    };
  }, [parent.ancestorSnippetNames, snippetName]);

  return <NestedSnippetContext.Provider value={value}>{children}</NestedSnippetContext.Provider>;
};

export const useIsNestedSnippet = () => {
  const { isNested } = useContext(NestedSnippetContext);
  return isNested;
};

export const useIsRecursiveSnippet = (snippetName: string) => {
  const { ancestorSnippetNames } = useContext(NestedSnippetContext);
  return ancestorSnippetNames.has(snippetName);
};
